// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {gql} from '@supermove/graphql';
import {
  useState,
  useQuery,
  useDebouncedCallback,
  useNavigation,
  useResponsive,
  useEffect,
} from '@supermove/hooks';

// Components
import Drawer from '@shared/design/components/Drawer';
import OutgoingCallDrawerContent from '@shared/modules/Call/components/OutgoingCallDrawerContent';
import InitiateOutgoingCallForm from '@shared/modules/Call/forms/InitiateOutgoingCallForm';
import useInitiateOutgoingCallMutation from '@shared/modules/Call/hooks/useInitiateOutgoingCallMutation';

interface OutgoingCallDrawerProps {
  drawer: any;
  viewer: any;
  numberToCall?: string;
  leadName?: string;
  initialSearch?: string;
}

const OutgoingCallDrawer = ({
  drawer,
  viewer,
  numberToCall,
  leadName,
  initialSearch,
}: OutgoingCallDrawerProps) => {
  const responsive = useResponsive();
  const {navigator} = useNavigation();
  const [search, setSearch] = useState(initialSearch || '');
  const [selectedOutboundNumber, setSelectedOutboundNumber] = useState('');
  const [selectedSalesLeadName, setSelectedSalesLeadName] = useState('');
  const [selectedSalesLeadId, setSelectedSalesLeadId] = useState('');
  const applySearch = useDebouncedCallback((text) => setSearch(text), 500);
  const {form, handleSubmit, submitting} = useInitiateOutgoingCallMutation({
    initiateOutgoingCallForm: InitiateOutgoingCallForm.new({
      fromUserId: viewer.id,
    }),
    onSuccess: (response) => {
      if (
        viewer.viewingOrganization.salesAppSettings.dialExternalProviderForOutboundCalls &&
        viewer.outboundOriginationPhoneNumber
      ) {
        const phoneLink = `tel:+1${viewer.outboundOriginationPhoneNumber.number}`;
        const link = document.createElement('a');
        link.href = phoneLink;
        link.click();
      }
      navigator.navigate('CallDetailsPage', {
        callUuid: response.call.uuid,
      });
    },
    onError: (errors: any) => {
      console.log({errors});
    },
  });

  const handleUpdateSearch = (text: string) => {
    applySearch(text);
    setSelectedSalesLeadId('');
    setSelectedSalesLeadName('');
  };

  const {loading, data} = useQuery(OutgoingCallDrawer.searchQuery, {
    fetchPolicy: 'cache-and-network',
    skip: !drawer.isOpen || search.length < 2,
    variables: {
      searchQuery: search,
    },
  });

  const handleSelectedOutboundNumber = (phoneNumber: any) => {
    form.setFieldValue('initiateOutgoingCallForm.outboundPhoneNumberId', phoneNumber.id);
    setSelectedOutboundNumber(phoneNumber.number);
  };

  // When a phone number is selected, if the organization setting is enabled, default the outbound number to a
  // phone number that matches the area code of the callee phone number.
  // If no match is found or the setting is off, default to the user's outbound number.
  const handlePhoneNumberSelected = ({toNumber, toName, toId}: any) => {
    form.setFieldValue('initiateOutgoingCallForm.toNumber', toNumber);
    setSelectedSalesLeadName(toName);
    setSelectedSalesLeadId(toId);
    if (viewer.viewingOrganization.settings.shouldDefaultOutboundNumberByAreaCode) {
      const matchString = toNumber.slice(0, 3);
      const businessNumberMatch = _.find(
        viewer.viewingOrganization.verifiedBusinessPhoneNumbers,
        (phoneNumber) => {
          return phoneNumber.number.startsWith(matchString);
        },
      );
      if (businessNumberMatch) {
        handleSelectedOutboundNumber(businessNumberMatch);
        return;
      }
      const userOutboundNumberMatch = _.find(viewer.verifiedPhoneNumbers, (phoneNumber) => {
        return phoneNumber.number.startsWith(matchString);
      });
      if (userOutboundNumberMatch) {
        handleSelectedOutboundNumber(userOutboundNumberMatch);
        return;
      }
    }
    if (viewer.viewingOrganization.features.isEnabledSalesForceUserToSelectOutboundNumber) {
      return;
    }
    handleSelectedOutboundNumber(viewer.outboundPhoneNumber);
  };

  useEffect(() => {
    // Wait for loading to finish before setting the number to call
    if (numberToCall) {
      handlePhoneNumberSelected({toNumber: numberToCall, toName: leadName, toId: ''});
    }
  }, [numberToCall, leadName]);

  return (
    <React.Fragment>
      <Drawer
        isOpen={drawer.isOpen}
        handleClose={() => {
          drawer.handleClose();
          setSearch('');
        }}
        width={Drawer.WIDTH.DEFAULT}
      >
        <Drawer.Header
          headerText={'New Call'}
          handleClose={() => {
            drawer.handleClose();
            setSearch('');
          }}
          isClosable
          isResponsive
        />
        <OutgoingCallDrawerContent
          search={search}
          handleUpdateSearch={handleUpdateSearch}
          filteredSalesLeads={data?.viewer?.viewingOrganization?.filteredSalesLeads}
          loading={loading}
          handlePhoneNumberSelected={handlePhoneNumberSelected}
          submitting={submitting}
          form={form}
          responsive={responsive}
          handleSubmit={handleSubmit}
          viewer={viewer}
          handleSelectedOutboundNumber={handleSelectedOutboundNumber}
          selectedOutboundNumber={selectedOutboundNumber}
          selectedSalesLeadName={selectedSalesLeadName}
          selectedSalesLeadId={selectedSalesLeadId}
        />
      </Drawer>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OutgoingCallDrawer.searchQuery = gql`
  ${OutgoingCallDrawerContent.searchFragment}

  query OutgoingCallDrawerSearch($searchQuery: String) {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        ...OutgoingCallDrawerContent_search
      }
    }
  }
`;

OutgoingCallDrawer.fragment = gql`
  ${OutgoingCallDrawerContent.initialFragment}

  fragment OutgoingCallDrawer on User {
    id
    outboundPhoneNumber {
      id
      number
    }
    outboundOriginationPhoneNumber {
      id
      number
    }
    verifiedPhoneNumbers {
      id
      number
    }
    viewingOrganization {
      id
      settings {
        id
        shouldDefaultOutboundNumberByAreaCode
      }
      salesAppSettings {
        id
        dialExternalProviderForOutboundCalls
      }
      verifiedBusinessPhoneNumbers {
        id
        number
      }
      features {
        isEnabledSalesForceUserToSelectOutboundNumber: isEnabled(
          feature: "SALES_FORCE_USER_TO_SELECT_OUTBOUND_NUMBER"
        )
      }
    }
    ...OutgoingCallDrawerContent_initial
  }
`;

OutgoingCallDrawer.callFragment = gql`
  fragment OutgoingCallDrawer_call on Call {
    id
    lead {
      id
      client {
        id
        primaryContact {
          id
          fullName
        }
      }
    }
  }
`;

export default OutgoingCallDrawer;
